/**
 * useHeartbeatSubscription Hook
 *
 * Subscribes to real-time heartbeat and status change events via WebSocket
 * Automatically updates React Query cache when events are received
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { socketService } from '../services/socket';
import type { Site } from './useSites';

interface HeartbeatEvent {
  type: 'heartbeat';
  site_id: string;
  timestamp: string;
  connection_status: string;
  operational_mode: string;
}

interface StatusChangeEvent {
  type: 'status_change';
  site_id: string;
  old_status: string;
  new_status: string;
  timestamp: string;
}

interface SitesResponse {
  sites: Site[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

export function useHeartbeatSubscription() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Connect to WebSocket if not already connected
    const socket = socketService.connect();

    if (!socket) {
      console.warn('⚠️ WebSocket not connected, heartbeat updates disabled');
      return;
    }

    // Subscribe to heartbeat events
    const handleHeartbeat = (event: HeartbeatEvent) => {
      // Update React Query cache
      queryClient.setQueriesData<{ data: SitesResponse }>(
        { queryKey: ['sites'] },
        (oldData) => {
          if (!oldData?.data?.sites) return oldData;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              sites: oldData.data.sites.map((site) =>
                site.site_id === event.site_id
                  ? {
                      ...site,
                      last_heartbeat: event.timestamp,
                      connection_status: event.connection_status as any,
                      operational_mode: event.operational_mode as any,
                    }
                  : site
              ),
            },
          };
        }
      );

      // Refresh fleet-level KPI aggregates (connection_status may change site online/degraded counts)
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregates'] });
    };

    // Subscribe to status change events
    const handleStatusChange = (event: StatusChangeEvent) => {
      // Update React Query cache
      queryClient.setQueriesData<{ data: SitesResponse }>(
        { queryKey: ['sites'] },
        (oldData) => {
          if (!oldData?.data?.sites) return oldData;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              sites: oldData.data.sites.map((site) =>
                site.site_id === event.site_id
                  ? {
                      ...site,
                      connection_status: event.new_status as any,
                    }
                  : site
              ),
            },
          };
        }
      );

      // Refresh fleet-level KPI aggregates and portfolio list (status change affects online/offline counts)
      queryClient.invalidateQueries({ queryKey: ['portfolio-aggregates'] });
      queryClient.invalidateQueries({ queryKey: ['portfolios'] });
    };

    // Register event listeners
    socket.on('heartbeat', handleHeartbeat);
    socket.on('status_change', handleStatusChange);

    // Cleanup on unmount
    return () => {
      socket.off('heartbeat', handleHeartbeat);
      socket.off('status_change', handleStatusChange);
    };
  }, [queryClient]);
}
